import React from 'react';
import { motion } from 'framer-motion';
export interface BudgetSegment {
  label: string;
  amount: number;
  color?: string;
}
export interface BudgetBarProps {
  totalBudget: number;
  segments: BudgetSegment[];
  showRemaining?: boolean;
  label?: string;
  mode?: 'stimulus' | 'support';
}
function formatEuro(value: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
}
const SEGMENT_COLORS = [{
  bg: 'bg-blue-500',
  light: 'bg-blue-100',
  text: 'text-blue-700',
  dot: 'bg-blue-500'
}, {
  bg: 'bg-amber-500',
  light: 'bg-amber-100',
  text: 'text-amber-700',
  dot: 'bg-amber-500'
}, {
  bg: 'bg-rose-500',
  light: 'bg-rose-100',
  text: 'text-rose-700',
  dot: 'bg-rose-500'
}, {
  bg: 'bg-purple-500',
  light: 'bg-purple-100',
  text: 'text-purple-700',
  dot: 'bg-purple-500'
}, {
  bg: 'bg-teal-500',
  light: 'bg-teal-100',
  text: 'text-teal-700',
  dot: 'bg-teal-500'
}, {
  bg: 'bg-orange-500',
  light: 'bg-orange-100',
  text: 'text-orange-700',
  dot: 'bg-orange-500'
}];
export function BudgetBar({
  totalBudget,
  segments,
  showRemaining = true,
  label,
  mode = 'support'
}: BudgetBarProps) {
  if (!totalBudget || totalBudget <= 0) {
    return <div className="bg-red-50 p-4 rounded-xl border border-red-200 text-center" data-id="element-12595" data-mp="vj94ol375zvi">
        <p className="text-red-600 text-sm font-medium" data-id="element-12596" data-mp="vh69lmn6tgjx">
          Geen budget opgegeven
        </p>
      </div>;
  }
  const validSegments = (segments || []).filter(s => s.label && typeof s.amount === 'number' && s.amount > 0);
  const totalSpent = validSegments.reduce((sum, s) => sum + s.amount, 0);
  const remaining = totalBudget - totalSpent;
  const overBudget = remaining < 0;
  const spentPct = Math.min(totalSpent / totalBudget * 100, 100);
  // ─── Stimulus mode: compact bar ─────────────────────────────────────
  if (mode === 'stimulus') {
    return <div className="bg-slate-50 rounded-xl px-4 py-3.5 border border-slate-200" data-id="element-12597" data-mp="xhtrw93al6nc">
        {label && <p className="text-xs font-semibold text-slate-700 mb-2.5 text-center" data-id="element-12598" data-mp="11hkmknvxw6x5">
            {label}
          </p>}

        {/* Budget header */}
        <div className="flex justify-between items-baseline mb-2" data-id="element-12599" data-mp="1b8f97td3l8j4">
          <span className="text-xs text-slate-500" data-id="element-12600" data-mp="ejhe2a1fr8lf">Budget</span>
          <span className="text-sm font-bold text-slate-800 font-mono tabular-nums" data-id="element-12601" data-mp="el0uq1r07lnq">
            {formatEuro(totalBudget)}
          </span>
        </div>

        {/* Bar */}
        <div className="h-5 bg-slate-200 rounded-full overflow-hidden flex" data-id="element-12602" data-mp="nj4il4pgouip">
          {validSegments.map((seg, i) => {
          const pct = seg.amount / totalBudget * 100;
          const colors = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          return <motion.div key={i} initial={{
            width: 0
          }} animate={{
            width: `${Math.min(pct, 100)}%`
          }} transition={{
            delay: i * 0.1,
            duration: 0.5,
            ease: 'easeOut'
          }} className={`${colors.bg} h-full ${i === 0 ? 'rounded-l-full' : ''}`} title={`${seg.label}: ${formatEuro(seg.amount)}`} data-id="element-12603" data-mp="23xmi9ma6hgim" data-mpi={i} />;
        })}
        </div>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2" data-id="element-12604" data-mp="30q2q51r05q26">
          {validSegments.map((seg, i) => {
          const colors = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          return <div key={i} className="flex items-center gap-1" data-id="element-12605" data-mp="2qsy02qu1dag3" data-mpi={i}>
                <div className={`w-2 h-2 rounded-full ${colors.dot}`} data-id="element-12606" data-mp="2qusncbss5enc" />
                <span className="text-xs text-slate-600" data-id="element-12607" data-mp="tzmjb14cps2d">{seg.label}</span>
              </div>;
        })}
          {showRemaining && remaining > 0 && <div className="flex items-center gap-1" data-id="element-12608" data-mp="x1hko8qpcve5">
              <div className="w-2 h-2 rounded-full bg-emerald-400" data-id="element-12609" data-mp="wzwo6dktv2dn" />
              <span className="text-xs text-emerald-600" data-id="element-12610" data-mp="120bzkzljtzsz">Over</span>
            </div>}
        </div>
      </div>;
  }
  // ─── Support mode: full educational panel ───────────────────────────
  return <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border-2 border-slate-200 shadow-sm overflow-hidden" data-id="element-12611" data-mp="s0tj4t4ukw5o">
      {/* Header */}
      <div className="px-5 py-3.5 border-b border-slate-200 bg-white/60" data-id="element-12612" data-mp="j2hwt3b0kkj1">
        <div className="flex items-center justify-between" data-id="element-12613" data-mp="4d3kpng1zozy">
          <div data-id="element-12614" data-mp="4en2jihcy83u">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-12615" data-mp="4az6ocofx9ly">
              {label || '💰 Budget overzicht'}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5" data-id="element-12616" data-mp="3rhrvs2jlx9ml">
              Totaal budget: {formatEuro(totalBudget)}
            </p>
          </div>
          <div className={`text-right px-3 py-1.5 rounded-lg ${overBudget ? 'bg-red-100' : 'bg-emerald-100'}`} data-id="element-12617" data-mp="3vi3cj48umpss">
            <div className="text-[10px] text-slate-500 uppercase tracking-wide" data-id="element-12618" data-mp="3cwxhlg4jgqkt">
              {overBudget ? 'Tekort' : 'Over'}
            </div>
            <div className={`text-sm font-bold font-mono tabular-nums ${overBudget ? 'text-red-700' : 'text-emerald-700'}`} data-id="element-12619" data-mp="3lw0xyccbmrvm">
              {formatEuro(Math.abs(remaining))}
            </div>
          </div>
        </div>
      </div>

      {/* Bar visualization */}
      <div className="px-5 py-4" data-id="element-12620" data-mp="3qvdk0uvdbux5">
        {/* The bar */}
        <div className="h-8 bg-slate-200 rounded-full overflow-hidden flex mb-4 relative" data-id="element-12621" data-mp="3qsrd8xgaxjfz">
          {validSegments.map((seg, i) => {
          const pct = seg.amount / totalBudget * 100;
          const colors = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          return <motion.div key={i} initial={{
            width: 0
          }} animate={{
            width: `${Math.min(pct, 100)}%`
          }} transition={{
            delay: i * 0.15,
            duration: 0.6,
            ease: 'easeOut'
          }} className={`${colors.bg} h-full flex items-center justify-center relative ${i === 0 ? 'rounded-l-full' : ''}`} data-id="element-12622" data-mp="ifbreqcqni5y" data-mpi={i}>
                {pct > 12 && <span className="text-[10px] font-bold text-white/90 truncate px-1" data-id="element-12623" data-mp="3pyyetcw8zf8f">
                    {Math.round(pct)}%
                  </span>}
              </motion.div>;
        })}
          {/* Remaining space */}
          {remaining > 0 && <div className="flex-1 h-full flex items-center justify-center" data-id="element-12624" data-mp="7sk21h9ptbbm">
              {remaining / totalBudget * 100 > 12 && <span className="text-[10px] font-semibold text-slate-400" data-id="element-12625" data-mp="24mdrj33910e5">
                  {Math.round(remaining / totalBudget * 100)}%
                </span>}
            </div>}
        </div>

        {/* Segment breakdown */}
        <div className="space-y-2" data-id="element-12626" data-mp="1x9xr0wj0lbmc">
          {validSegments.map((seg, i) => {
          const pct = seg.amount / totalBudget * 100;
          const colors = SEGMENT_COLORS[i % SEGMENT_COLORS.length];
          return <motion.div key={i} initial={{
            opacity: 0,
            x: -8
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: i * 0.1 + 0.3,
            duration: 0.3
          }} className={`flex items-center justify-between ${colors.light} rounded-lg px-3 py-2`} data-id="element-12627" data-mp="1ncjcx3kkcyx7" data-mpi={i}>
                <div className="flex items-center gap-2" data-id="element-12628" data-mp="zmfj1nx6gdje">
                  <div className={`w-2.5 h-2.5 rounded-full ${colors.dot}`} data-id="element-12629" data-mp="zjudooxo7fgw" />
                  <span className={`text-sm font-medium ${colors.text}`} data-id="element-12630" data-mp="vj0ta5jfsisr">
                    {seg.label}
                  </span>
                </div>
                <div className="flex items-baseline gap-2" data-id="element-12631" data-mp="2zoy9o8j9pavd">
                  <span className="text-xs text-slate-400" data-id="element-12632" data-mp="2zmc3odc0z3f9">
                    {Math.round(pct)}%
                  </span>
                  <span className={`text-sm font-bold font-mono tabular-nums ${colors.text}`} data-id="element-12633" data-mp="2qnqjyeuthicw">
                    {formatEuro(seg.amount)}
                  </span>
                </div>
              </motion.div>;
        })}

          {/* Remaining */}
          {showRemaining && <motion.div initial={{
          opacity: 0,
          x: -8
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: validSegments.length * 0.1 + 0.3,
          duration: 0.3
        }} className={`flex items-center justify-between rounded-lg px-3 py-2 ${overBudget ? 'bg-red-100' : 'bg-emerald-100'}`} data-id="element-12634" data-mp="34mgmwb4bic96">
              <div className="flex items-center gap-2" data-id="element-12635" data-mp="bbj49uip0dk8">
                <div className={`w-2.5 h-2.5 rounded-full ${overBudget ? 'bg-red-500' : 'bg-emerald-500'}`} data-id="element-12636" data-mp="b8wy9zbga644" />
                <span className={`text-sm font-medium ${overBudget ? 'text-red-700' : 'text-emerald-700'}`} data-id="element-12637" data-mp="k2rwene5mwa9">
                  {overBudget ? 'Tekort' : 'Over'}
                </span>
              </div>
              <span className={`text-sm font-bold font-mono tabular-nums ${overBudget ? 'text-red-700' : 'text-emerald-700'}`} data-id="element-12638" data-mp="3oj2ovxldbgk9">
                {formatEuro(Math.abs(remaining))}
              </span>
            </motion.div>}
        </div>
      </div>

      {/* Educational tip */}
      <div className="bg-white/70 px-5 py-3 border-t border-slate-100" data-id="element-12639" data-mp="1hsmtct4634zr">
        <div className="flex items-start gap-2.5" data-id="element-12640" data-mp="1crgximf7n1m0">
          <span className="text-slate-400 text-sm mt-0.5" data-id="element-12641" data-mp="2a9v6811flvur">💡</span>
          <p className="text-xs text-slate-700 leading-relaxed" data-id="element-12642" data-mp="2ab6by6eftdoi">
            <strong data-id="element-12643" data-mp="25bk0o2i667ic">Tip:</strong>{' '}
            {overBudget ? 'De uitgaven zijn hoger dan het budget. Kijk welke uitgave je kunt verlagen of weglaten.' : `Je hebt ${Math.round(remaining / totalBudget * 100)}% van je budget over. Dat is ${formatEuro(remaining)}.`}
          </p>
        </div>
      </div>
    </div>;
}